import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme } from "@mui/material";
import {
    Dashboard as DashboardIcon,
    Work as WorkIcon,
    Assignment as AssignmentIcon,
    EventNote as EventNoteIcon,
    People as PeopleIcon,
    Task as TaskIcon,
    Ballot as BallotIcon
} from "@mui/icons-material";
import { getUserRole } from "../utils/auth";
import logoIcon from "../assets/images/hashstack-logo-orange-black.png";

const drawerWidth = 260;

const roleNavItems = {
    manager: [
        { label: "Dashboard", path: "/manager/dashboard", icon: <DashboardIcon /> },
        { label: "Project Management", path: "/manager/projects", icon: <WorkIcon /> },
        { label: "Epic Management", path: "/manager/epic-management", icon: <AssignmentIcon /> },
        { label: "Story Management", path: "/manager/stories-management", icon: <BallotIcon /> },
        { label: "Task Management", path: "/manager/task-management", icon: <TaskIcon /> },
        { label: "Attendance", path: "/manager/attendance-management", icon: <EventNoteIcon /> },
        { label: "Leave Management", path: "/manager/leave-management", icon: <PeopleIcon /> },
        // { label: "Performance Management", path: "/manager/performance-management", icon: <BarChartIcon /> },
        // { label: "Payroll", path: "/employee/my-payroll", icon: <PaidIcon /> },
    ],
    hr: [
        { label: "Dashboard", path: "/hr/dashboard", icon: <DashboardIcon /> },
        { label: "Employee Management", path: "/hr/employee-management", icon: <PeopleIcon /> },
        { label: "Attendance Management", path: "/hr/attendance-management", icon: <EventNoteIcon /> },
        { label: "Leave Management", path: "/hr/leave-management", icon: <AssignmentIcon /> },
        // { label: "Performance Management", path: "/hr/performance-management", icon: <BarChartIcon /> },
        // { label: "Payroll Management", path: "/hr/payroll-management", icon: <MonetizationOnIcon /> },
    ],
    "team lead": [
        { label: "Dashboard", path: "/team-lead/dashboard", icon: <DashboardIcon /> },
        { label: "Project Management", path: "/team-lead/projects", icon: <WorkIcon /> },
        { label: "Epic Management", path: "/team-lead/epic-management", icon: <AssignmentIcon /> },
        { label: "Story Management", path: "/team-lead/stories-management", icon: <BallotIcon /> },
        { label: "Task Management", path: "/team-lead/task-management", icon: <TaskIcon /> },
        { label: "Attendance", path: "/team-lead/attendance-management", icon: <EventNoteIcon /> },
        { label: "Leave Management", path: "/team-lead/leave-management", icon: <PeopleIcon /> },
        // { label: "Performance Management", path: "/team-lead/performance-management", icon: <BarChartIcon /> },
        // { label: "Payroll", path: "/employee/my-payroll", icon: <PaidIcon /> },
    ],
    employee: [
        { label: "Dashboard", path: "/employee/dashboard", icon: <DashboardIcon /> },
        { label: "My Projects", path: "/employee/my-project-list", icon: <WorkIcon /> },
        { label: "My Tasks", path: "/employee/my-tasks", icon: <AssignmentIcon /> },
        { label: "My Attendance", path: "/employee/my-attendance", icon: <EventNoteIcon /> },
        { label: "My Leaves", path: "/employee/manage-leave-requests", icon: <PeopleIcon /> },
        // { label: "My Performance", path: "/employee/my-performance", icon: <BarChartIcon /> },
        // { label: "Payroll", path: "/employee/my-payroll", icon: <PaidIcon /> },
    ],
};

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const rawRole = getUserRole() || "employee";
    const role = rawRole.toLowerCase().trim();
    const navItems = roleNavItems[role] || roleNavItems.employee;

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: "#2c3e50" }}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                    sx={{
                        px: 2,
                        py: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
                    }}
                >
                    <Box
                        component="img"
                        src={logoIcon}
                        alt="HashStack Logo"
                        sx={{
                            width: "auto",
                            maxHeight: "80px",
                            maxWidth: "100%",
                            objectFit: "contain",
                            mb: 0.5
                        }}
                    />
                </Box>
            </Box>

            <List sx={{ px: 1 }}>
                {navItems.map((item) => {
                    const isDashboard = item.path.endsWith('dashboard');
                    const selected = isDashboard
                        ? location.pathname === item.path
                        : location.pathname.startsWith(item.path);
                    return (
                        <ListItemButton
                            key={item.path}
                            selected={selected}
                            onClick={() => {
                                navigate(item.path);
                                if (isMobile) handleDrawerToggle();
                            }}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                color: selected ? "#fff" : "rgba(255,255,255,0.6)",
                                backgroundColor: selected ? "#2980B9 !important" : "transparent",
                                '&:hover': { backgroundColor: "rgba(255,255,255,0.05)" },
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 40, mr: -1.5 }}>
                                {React.cloneElement(item.icon, { fontSize: 'small' })}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: selected ? 600 : 400 }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
                }}
            >
                {drawerContent}
            </Drawer>

            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
                }}
                open
            >
                {drawerContent}
            </Drawer>
        </Box>
    );
};

export default Sidebar;